import React, { useEffect, useRef, useState } from 'react'
import SearchField from '../components/SearchField'
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import SearchIcon from '@material-ui/icons/Search'
import InputAdornment from '@material-ui/core/InputAdornment'
import Layout from '../components/layout/Layout'
import Pagination from '../components/common/Pagination'
import api from '../api/client'
import { useRecoilState } from 'recoil'
import { userInfoState } from '../recoil/atoms'
import { beginImpersonation } from '../api/impersonation'
import { toFarsiDigits, gregorianToJalali } from '../assets/utils'
import { toast } from '../api/toast'

const KIND_LABEL = {
  login_code: 'کد ورود',
  welcome: 'تایید ورود',
  sub_status: 'تغییر اشتراک',
  payment: 'پرداخت/فاکتور',
  new_transfer: 'حواله جدید',
  insurance: 'بیمه',
  expiry: 'انقضای اشتراک',
  other: 'سایر',
}

const thStyle = {
  backgroundColor: '#1a237e',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: 12.5,
  whiteSpace: 'nowrap',
}

// کارت آماری گرادیانی — همسان با صفحهٔ تاریخچهٔ پیامک «ارجان فیش»
const StatCard = ({ label, value, sub, gradient, shadow }) => (
  <div style={{
    borderRadius: 14,
    padding: '18px 20px',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
    background: gradient,
    boxShadow: shadow,
  }}>
    <div style={{ fontSize: 12.5, opacity: 0.88 }}>{label}</div>
    <div style={{
      fontSize: 26, fontWeight: 800, marginTop: 4,
      fontVariantNumeric: 'tabular-nums',
    }}>{value}</div>
    <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 2 }}>{sub}</div>
  </div>
)

const STAT_GRID = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(168px, 1fr))',
  gap: 14,
  marginBottom: 18,
}

const useStyles = makeStyles(() => ({
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  filterControl: { minWidth: 160 },
  errorText: { color: '#c62828', fontSize: 11.5, whiteSpace: 'pre-line', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' },
}))

const formatDate = (isoDateStr) => {
  if (!isoDateStr) return '-'
  try {
    const d = new Date(isoDateStr)
    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    return toFarsiDigits(`${gregorianToJalali(d.toISOString().slice(0, 10))} ${hhmm}`)
  } catch (e) { return '-' }
}

const SmsLogs = () => {
  const classes = useStyles()
  const [userInfo, setUserInfo] = useRecoilState(userInfoState)

  // ورود به حساب پیمانکار/کاربر از تاریخچهٔ پیامک — مثل لیست کاربران
  const loginAs = (row) => {
    if (!row || !row.user_id) return
    beginImpersonation(userInfo)
    const url = row.contractor_id
      ? `users/login/${row.contractor_id}`
      : `users/login/${row.user_id}?by=user`
    api.post(url).then((result) => {
      if (result.status === 200) {
        setUserInfo(result.data.user)
        localStorage.setItem('access', result.data.tokens.access)
        if (result.data.tokens.refresh) {
          localStorage.setItem('refresh', result.data.tokens.refresh)
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + result.data.tokens.access
      }
    })
  }

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, success: 0, failed: 0 })
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [kind, setKind] = useState('')
  const [period, setPeriod] = useState('day')
  const [byKind, setByKind] = useState([])
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [purgeOpen, setPurgeOpen] = useState(false)
  const [purgeDate, setPurgeDate] = useState('')
  const [purging, setPurging] = useState(false)
  const [credit, setCredit] = useState(null)
  const [reload, setReload] = useState(0)
  const seq = useRef(0)

  // جستجوی زنده هنگام تایپ (دلی ۴۰۰ms)
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1) }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setLoading(true)
    const mySeq = ++seq.current
    api.get(`users/sms-logs/?page=${page}&period=${period}${kind ? '&kind=' + kind : ''}${search ? '&search=' + encodeURIComponent(search) : ''}`)
      .then((res) => {
        if (seq.current !== mySeq) return
        setRows(res.data.results)
        setSummary(res.data.summary)
        setTotalPages(res.data.total_pages)
        setCredit(res.data.credit || null)
        setByKind(res.data.by_kind || [])
        setLoading(false)
      })
      .catch(() => { setLoading(false) })
  }, [page, kind, search, period, reload])

  const today = new Date().toISOString().slice(0, 10)

  const doPurge = async () => {
    if (!purgeDate) return
    setPurging(true)
    try {
      // اول شمارش، بعد حذف
      const r = await api.delete(`users/sms-logs/`, { data: { until: purgeDate } })
      toast(`حذف شد: ${toFarsiDigits(r.data.deleted)} رکورد تا ${toFarsiDigits(purgeDate)}`, 'success')
      setPurgeOpen(false)
      setPage(1)
      // رفرش لیست
      seq.current += 1
      const res = await api.get(`users/sms-logs/?page=1${kind ? '&kind=' + kind : ''}`)
      setRows(res.data.results)
      setSummary(res.data.summary)
      setTotalPages(res.data.total_pages)
    } catch (err) {
      toast(err?.response?.data?.detail || 'خطا در حذف', 'error')
    } finally {
      setPurging(false)
    }
  }

  return (
    <Layout>
      <Card elevation={5} style={{ borderRadius: 14, direction: 'rtl' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>تاریخچه پیامک‌ها</Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => setReload((x) => x + 1)}
                disabled={loading}
                style={{ color: '#0b3d4f', borderColor: '#94a3b8' }}>
                🔄 به‌روزرسانی
              </Button>
              <Button size="small" variant="outlined" startIcon={<DeleteIcon />}
                onClick={() => { setPurgeDate(''); setPurgeOpen(true) }}
                style={{ color: '#c62828', borderColor: '#ef9a9a' }}>
                حذف تا تاریخ
              </Button>
            </Box>
          </Box>

          <div style={STAT_GRID}>
            <StatCard label="کل پیامک‌های ارسالی" value={toFarsiDigits(summary.total)}
              sub="رکورد در تاریخچه"
              gradient="linear-gradient(135deg,#0b3d4f,#0f6e7d)"
              shadow="0 6px 18px rgba(11,61,79,.28)" />
            <StatCard label="موفق" value={toFarsiDigits(summary.success)}
              sub={summary.total ? toFarsiDigits(Math.round((summary.success / summary.total) * 100)) + '٪ از کل' : '—'}
              gradient="linear-gradient(135deg,#059669,#10b981)"
              shadow="0 6px 18px rgba(5,150,105,.28)" />
            <StatCard label="ناموفق" value={toFarsiDigits(summary.failed)}
              sub={summary.total ? toFarsiDigits(Math.round((summary.failed / summary.total) * 100)) + '٪ از کل' : '—'}
              gradient="linear-gradient(135deg,#b91c1c,#ef4444)"
              shadow="0 6px 18px rgba(185,28,28,.28)" />
            <StatCard label="امروز" value={toFarsiDigits(summary.today || 0)}
              sub="پیامک امروز"
              gradient="linear-gradient(135deg,#f59e0b,#ea580c)"
              shadow="0 6px 18px rgba(234,88,12,.28)" />
            {credit && credit.credit != null && (
              <StatCard label="مانده اعتبار کاوه‌نگار"
                value={toFarsiDigits(Number(credit.credit).toLocaleString('en-US'))}
                sub={'ریال' + ((credit.min_alarm && credit.credit < credit.min_alarm)
                  ? ' — کمتر از حد هشدار ⚠️' : ' — کافی ✓')}
                gradient={(credit.min_alarm && credit.credit < credit.min_alarm)
                  ? 'linear-gradient(135deg,#b91c1c,#ef4444)'
                  : 'linear-gradient(135deg,#047857,#10b981)'}
                shadow={(credit.min_alarm && credit.credit < credit.min_alarm)
                  ? '0 6px 18px rgba(185,28,28,.28)'
                  : '0 6px 18px rgba(4,120,87,.28)'} />
            )}
          </div>

          {/* بازهٔ زمانی */}
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" style={{ marginTop: 14 }}>
            <span style={{ fontSize: 12.5, color: '#607d8b', fontWeight: 700 }}>بازه:</span>
            {[
              { v: 'day', t: 'امروز (۲۴ ساعت)' },
              { v: 'week', t: 'هفتهٔ اخیر' },
              { v: 'month', t: 'ماه اخیر' },
              { v: 'all', t: 'همه' },
            ].map((o) => (
              <span
                key={o.v}
                onClick={() => { setPeriod(o.v); setPage(1) }}
                style={{
                  cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                  borderRadius: 999, padding: '5px 14px',
                  background: period === o.v ? '#0b3d4f' : '#fff',
                  color: period === o.v ? '#fff' : '#0b3d4f',
                  border: '1px solid ' + (period === o.v ? '#0b3d4f' : '#cbd5e1'),
                }}
              >
                {o.t}
              </span>
            ))}
          </Box>

          {/* تفکیک بر اساس نوع پیامک در همین بازه */}
          {byKind.length > 0 && (
            <Box style={{
              marginTop: 12, border: '1px solid #e2e8f0', borderRadius: 12,
              overflow: 'hidden', background: '#fff',
            }}>
              <Box style={{ padding: '8px 12px', background: '#f8fafc', fontWeight: 800, fontSize: 12.5, color: '#0b3d4f' }}>
                تفکیک بر اساس نوع پیامک — {period === 'day' ? '۲۴ ساعت اخیر' : period === 'week' ? 'هفتهٔ اخیر' : period === 'month' ? 'ماه اخیر' : 'کل'}
              </Box>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 12px' }}>
                {byKind.map((b) => (
                  <span
                    key={b.kind}
                    onClick={() => { setKind(kind === b.kind ? '' : b.kind); setPage(1) }}
                    style={{
                      cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                      borderRadius: 10, padding: '6px 12px',
                      background: kind === b.kind ? '#0b3d4f' : '#eef6f8',
                      color: kind === b.kind ? '#fff' : '#0b3d4f',
                      border: '1px solid ' + (kind === b.kind ? '#0b3d4f' : '#cfe3e8'),
                    }}
                    title={b.failed ? `${toFarsiDigits(b.failed)} ناموفق` : 'همه موفق'}
                  >
                    {b.label}: {toFarsiDigits(b.total)}
                    {b.failed ? <span style={{ color: '#fca5a5', fontWeight: 700 }}> ({toFarsiDigits(b.failed)}✗)</span> : null}
                  </span>
                ))}
              </Box>
            </Box>
          )}

          <Box className={classes.toolbar} style={{ marginTop: 14 }}>
            <SearchField
              placeholder="جستجو در شماره گیرنده یا متن..."
              variant="outlined" size="small" className={classes.filterControl}
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              style={{ minWidth: 280, background: '#fff' }}
            />
            <FormControl variant="outlined" size="small" className={classes.filterControl}>
              <InputLabel>نوع پیامک</InputLabel>
              <Select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1) }} label="نوع پیامک">
                <MenuItem value="">همه</MenuItem>
                {Object.entries(KIND_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper} style={{ overflowX: 'auto', }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={thStyle}>ورود</TableCell>
                  <TableCell style={thStyle}>زمان</TableCell>
                  <TableCell style={thStyle}>نوع</TableCell>
                  <TableCell style={thStyle}>گیرنده</TableCell>
                  <TableCell style={thStyle}>نام گیرنده</TableCell>
                  <TableCell style={thStyle}>ادارهٔ کارفرما</TableCell>
                  <TableCell style={thStyle}>اشتراک</TableCell>
                  <TableCell style={thStyle}>وضعیت</TableCell>
                  <TableCell style={thStyle}>متن / خطا</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} align="center">در حال بارگذاری...</TableCell></TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center">پیامکی یافت نشد</TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell align="center" dir="ltr" style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1a237e' }}>
                      {r.login_code ? (
                        <div style={{ fontSize: 12 }}>{toFarsiDigits(r.login_code)}</div>
                      ) : null}
                      {r.user_id ? (
                        <Button
                          variant="contained"
                          startIcon={<ExitToAppIcon style={{ fontSize: 16 }} />}
                          size="small"
                          variant="outlined"
                          onClick={() => loginAs(r)}
                          title="ورود به حساب این کاربر"
                          style={{
                            marginTop: 3, minWidth: 0, padding: '3px 14px',
                            fontSize: 12, fontWeight: 'bold', textTransform: 'none',
                            backgroundColor: '#1a237e', color: '#fff', borderRadius: 6,
                            boxShadow: '0 1px 3px rgba(26,35,126,.35)',
                          }}
                        >
                          ورود
                        </Button>
                      ) : (
                        !r.login_code && <span style={{ color: '#b0bec5' }}>—</span>
                      )}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(r.created_at)}</TableCell>
                    <TableCell><Chip size="small" label={KIND_LABEL[r.kind] || r.kind} style={{ background: '#e8eaf6', color: '#283593', fontWeight: 700 }} /></TableCell>
                    <TableCell dir="ltr" style={{ fontFamily: 'monospace' }}>{toFarsiDigits(r.recipient)}</TableCell>
                    <TableCell style={{ fontSize: 11.5, color: '#3949ab', fontWeight: 700 }}>{r.name || r.user || '—'}</TableCell>
                    <TableCell style={{ fontSize: 11.5, color: '#00695c', fontWeight: 700 }}>
                      {r.department || '—'}
                      {r.contract_number ? (
                        <div style={{ fontSize: 10.5, color: '#78909c', fontWeight: 600 }}>
                          قرارداد {toFarsiDigits(r.contract_number)}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell align="center">
                      {r.is_subscribed ? (
                        <Chip size="small" label="فعال" style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
                      ) : (
                        <Chip size="small" label="غیرفعال" style={{ background: '#fbe9e7', color: '#c62828', fontWeight: 700 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">{r.success ? '✓' : '✗'}</TableCell>
                    <TableCell style={{ maxWidth: 340 }}>
                      <Tooltip title={r.success ? r.text : (r.error || r.text)} arrow placement="top">
                        <span className={classes.errorText} style={{ color: r.success ? '#455a64' : '#c62828' }}>
                          {r.success ? r.text : (r.error || 'خطا')}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination page={page} pages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      {/* دیالوگ حذف تا تاریخ */}
      <Dialog open={purgeOpen} onClose={() => !purging && setPurgeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>حذف سابقهٔ پیامک‌ها تا تاریخ</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 13, color: '#5c6370', marginBottom: 12 }}>
            همهٔ رکوردهای ارسال‌شده تا پایان تاریخِ واردشده حذف می‌شوند (از رکوردهای بعد از آن چیزی حذف نمیشود).
          </Typography>
          <TextField
            type="date" fullWidth variant="outlined" size="small"
            value={purgeDate} onChange={(e) => { setPurgeDate(e.target.value) }}
            InputLabelProps={{ shrink: true }} dir="ltr"
            inputProps={{ max: today }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)} disabled={purging}>انصراف</Button>
          <Button variant="contained" color="primary" style={{ background: '#c62828' }}
            disabled={!purgeDate || purging} onClick={doPurge}>
            {purging ? 'در حال حذف...' : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  )
}

export default SmsLogs
